import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';
import { kaspreadsheetUrl } from '@/mocks/categories';

export default function BaseTaoBeginner() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildArticleJsonLd({ headline: 'BaseTao Spreadsheet for Beginners: A Complete Getting Started Guide', description: 'A beginner-friendly guide to BaseTao Spreadsheet covering account creation, setup, and placing your first order — no experience required.', path: '/basetao-spreadsheet-beginner' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Beginner Guide</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Spreadsheet for Beginners: A Complete Getting Started Guide
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                New to BaseTao Spreadsheet? This beginner-friendly guide walks you through everything from account creation to placing your first order. No prior experience with spreadsheets or international sourcing required — we cover every step in clear, simple language.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>12 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Welcome to BaseTao Spreadsheet</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  If you are new to international product sourcing, the idea of managing orders across multiple suppliers, tracking dozens of products, calculating shipping costs, and monitoring warehouse inventory might seem overwhelming. BaseTao Spreadsheet was designed specifically to simplify this entire process — bringing order and clarity to what would otherwise be a chaotic collection of browser tabs, sticky notes, and manual spreadsheets.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  This guide assumes zero prior experience. You do not need to be a spreadsheet expert, a logistics professional, or an experienced international buyer to get value from BaseTao Spreadsheet. The platform is designed to be approachable for complete beginners while offering enough depth to grow with you as your skills and sourcing volume increase over time.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Step-by-Step Beginner Workflow</h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Create Your Account', desc: 'Setting up a BaseTao Spreadsheet account takes less than five minutes. You will need a valid email address and a secure password.' },
                    { step: '2', title: 'Configure Your Preferences', desc: 'Set your preferred currency, measurement units, and default shipping destination. These settings ensure all price comparisons and shipping calculations are accurate for your location.' },
                    { step: '3', title: 'Create Your First Spreadsheet', desc: 'Choose the Beginner template, which includes pre-configured columns and helpful tooltips that explain each field as you hover over it.' },
                    { step: '4', title: 'Add Your First Product', desc: 'Find a product you are interested in and paste its URL into your spreadsheet. Watch as the platform automatically populates the product details for you.' },
                    { step: '5', title: 'Explore and Learn', desc: 'As you add more products, explore the comparison tools, shipping calculator, and warehouse features at your own pace.' },
                  ].map((item) => (
                    <div key={item.step} className="flex gap-4 bg-background-100 rounded-lg p-5 border border-background-200/60">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">{item.step}</div>
                      <div>
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Common Beginner Mistakes to Avoid</h2>
                <div className="space-y-3">
                  {[
                    'Skipping the preference setup and discovering later that all prices were displayed in the wrong currency',
                    'Adding too many products at once before understanding how the comparison tools work',
                    'Ignoring the shipping calculator and being surprised by international shipping costs',
                    'Forgetting to check warehouse storage timelines and incurring unnecessary storage fees',
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/60">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-sm text-accent-500"></i>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{tip}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Ready to Begin Your Journey?</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                  The best way to learn is by doing. Visit our <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">homepage</Link> for more resources, or jump straight into the platform.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                    Get Started Now
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}